/**
 * Speaker-toggle context for DeepThink chimes (DESIGN.md §11).
 *
 * Reads the user's sound preference from localStorage on mount, seeds the
 * module-level singleton in `notificationSounds.ts`, and exposes toggle
 * state + a first-visit flag. The Snackbar toast is not rendered here —
 * consumers mount `<SoundWelcomeToast>` where they want it (Playground
 * screens only per §3). This keeps the provider safe to mount globally
 * without leaking a welcome toast onto marketing routes.
 *
 * Sound-enabled default: TRUE (DESIGN.md §11/§16). A missing localStorage
 * key signals "first visit"; after the first provider mount we persist
 * `true` so the welcome toast only shows once.
 */
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  isSoundEnabled as readSingleton,
  setSoundEnabled as writeSingleton,
} from '@/utils/notificationSounds';

export const SOUND_ENABLED_STORAGE_KEY = 'deepthink.soundEnabled';

interface SoundContextValue {
  /** Whether chimes will play. Drives speaker-icon visual state. */
  enabled: boolean;
  /** Flip the preference; persists to localStorage and singleton. */
  toggle: () => void;
  /** Set explicitly; persists to localStorage and singleton. */
  setEnabled: (next: boolean) => void;
  /** True only on the very first provider mount in this browser. Flips to
   *  false after `markWelcomeSeen()` so the welcome toast shows once. */
  isFirstVisit: boolean;
  /** Call after rendering the welcome toast so it never renders again. */
  markWelcomeSeen: () => void;
}

const SoundContext = createContext<SoundContextValue | null>(null);

function readStoredPreference(): { enabled: boolean; isFirstVisit: boolean } {
  if (typeof window === 'undefined') {
    return { enabled: true, isFirstVisit: false };
  }
  try {
    const raw = window.localStorage.getItem(SOUND_ENABLED_STORAGE_KEY);
    if (raw === null) {
      return { enabled: true, isFirstVisit: true };
    }
    return { enabled: raw !== 'false', isFirstVisit: false };
  } catch {
    // Private-browsing or storage disabled — fall back to in-memory default.
    return { enabled: true, isFirstVisit: false };
  }
}

function writeStoredPreference(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      SOUND_ENABLED_STORAGE_KEY,
      enabled ? 'true' : 'false'
    );
  } catch {
    // Silent: storage may be unavailable (private browsing). Singleton still
    // tracks the value for the current session.
  }
}

interface SoundProviderProps {
  children: ReactNode;
  /** Test override: force an initial preference regardless of localStorage. */
  initialEnabled?: boolean;
}

export function SoundProvider({
  children,
  initialEnabled,
}: SoundProviderProps) {
  const [{ enabled, isFirstVisit }, setState] = useState<{
    enabled: boolean;
    isFirstVisit: boolean;
  }>(() => {
    if (typeof initialEnabled === 'boolean') {
      return { enabled: initialEnabled, isFirstVisit: false };
    }
    return readStoredPreference();
  });

  // Seed the module singleton on mount and on every change, so chimes
  // called from anywhere read the current preference.
  useEffect(() => {
    writeSingleton(enabled);
  }, [enabled]);

  // Persist the default on first visit so the welcome toast shows once.
  useEffect(() => {
    if (isFirstVisit) {
      writeStoredPreference(enabled);
    }
    // Only run on initial mount — subsequent changes persist via setEnabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    writeStoredPreference(next);
    writeSingleton(next);
    setState(prev => ({ ...prev, enabled: next }));
  }, []);

  const toggle = useCallback(() => {
    setState(prev => {
      const next = !prev.enabled;
      writeStoredPreference(next);
      writeSingleton(next);
      return { ...prev, enabled: next };
    });
  }, []);

  const markWelcomeSeen = useCallback(() => {
    setState(prev => ({ ...prev, isFirstVisit: false }));
  }, []);

  const value = useMemo<SoundContextValue>(
    () => ({
      enabled,
      toggle,
      setEnabled,
      isFirstVisit,
      markWelcomeSeen,
    }),
    [enabled, isFirstVisit, markWelcomeSeen, setEnabled, toggle]
  );

  return (
    <SoundContext.Provider value={value}>{children}</SoundContext.Provider>
  );
}

export function useSound(): SoundContextValue {
  const value = useContext(SoundContext);
  if (!value) {
    throw new Error('useSound must be used inside <SoundProvider>');
  }
  return value;
}

/** Non-throwing variant for defensive callers (e.g. shared components that
 *  might render outside the Playground shell). Returns the current singleton
 *  value when no provider is in scope. */
export function useOptionalSound(): SoundContextValue | null {
  const value = useContext(SoundContext);
  if (value) return value;
  return {
    enabled: readSingleton(),
    toggle: () => {},
    setEnabled: () => {},
    isFirstVisit: false,
    markWelcomeSeen: () => {},
  };
}
